import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  TextInput,
  Select,
  Button,
  Grid,
  Box,
  Group,
  Badge,
  Tooltip,
  Loader,
  Text,
  Popover,
  Stack,
  Divider,
  Modal,
  Paper,
} from "@mantine/core";
import { Instructor } from "../types/batch.types";
import {
  IconSend,
  IconCheck,
  IconX,
  IconHistory,
  IconEye,
  IconEdit,
  IconRefresh,
} from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { ApiService } from "../../api/api-service";
import { getInstructorLeaveCount, syncBatchInstructorLeaves } from "../utils/journeyLeave.utils";

interface InstructorFormProps {
  instructor: Instructor;
  index: number;
  instructorsDropdown: any[];
  onInstructorChange: (
    index: number,
    field: keyof Instructor,
    value: any,
    isEdit?: boolean,
  ) => void;
  onInstructorEmailChange?: (
    index: number,
    email: string | null,
    isEdit?: boolean,
  ) => void;
  onRemove: (index: number) => void;
  editMode: boolean;
  isEditBatch: boolean;
  batchNo?: string;
  courseName?: string;
  isSuperAdmin?: boolean;
}

interface MOURecord {
  requestId: string;
  status: string;
  createdAt: string;
  mouVersion?: number;
  numOfStud?: string;
}

// Convert decimal hours (e.g., 7.5) into "Xh Ym" format
export const formatDecimalHoursToTime = (value: number | string | undefined): string | null => {
  if (value === undefined || value === null || value === "") return null;

  const decimalHours = parseFloat(String(value));
  if (isNaN(decimalHours) || decimalHours < 0) return null;

  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  // Handle edge case where rounding minutes pushes to 60
  if (minutes === 60) {
    return `${hours + 1}h 0m`;
  }

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};


/**
 * Parses an admin-typed duration into decimal hours.
 * Accepts "16h 55m", "16h", "55m", "16:55", or a plain decimal ("16.92").
 * Returns null when the text can't be understood.
 */
export const parseDurationToDecimalHours = (input: string): number | null => {
  const text = input.trim().toLowerCase();
  if (!text) return null;

  if (/^\d+(\.\d+)?$/.test(text)) return Number(Number(text).toFixed(2));

  let hours = 0;
  let minutes = 0;
  const colon = text.match(/^(\d+):([0-5]?\d)$/);
  if (colon) {
    hours = Number(colon[1]);
    minutes = Number(colon[2]);
  } else {
    const hm = text.match(/^(?:(\d+)\s*h(?:rs?|ours?)?)?\s*(?:(\d+)\s*m(?:in(?:s|utes?)?)?)?$/);
    if (!hm || (hm[1] === undefined && hm[2] === undefined)) return null;
    hours = Number(hm[1] || 0);
    minutes = Number(hm[2] || 0);
    if (minutes > 59) return null;
  }
  return Number((hours + minutes / 60).toFixed(2));
};

export const InstructorForm: React.FC<InstructorFormProps> = ({
  instructor,
  index,
  instructorsDropdown,
  onInstructorChange,
  onInstructorEmailChange,
  onRemove,
  editMode,
  isEditBatch,
  batchNo,
  courseName,
  isSuperAdmin = false,
}) => {
  const [sendingMOU, setSendingMOU] = useState(false);
  const [mouStatus, setMouStatus] = useState<string | null>(null);
  const [mouCount, setMouCount] = useState<number>(0);
  const [mouHistory, setMouHistory] = useState<MOURecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);

  // ── Journey-based leave count ──
  const [journeyLeaveCount, setJourneyLeaveCount] = useState<number | null>(
    null,
  );
  const [loadingLeaveCount, setLoadingLeaveCount] = useState(false);

  // ── Manual attended-hours correction (superAdmin only) ──
  const [attendedHoursDraft, setAttendedHoursDraft] = useState(
    formatDecimalHoursToTime(instructor.attendedHours) || "",
  );
  const [savingAttendedHours, setSavingAttendedHours] = useState(false);

  useEffect(() => {
    setAttendedHoursDraft(formatDecimalHoursToTime(instructor.attendedHours) || "");
  }, [instructor.attendedHours]);

  const parsedDraftHours = parseDurationToDecimalHours(attendedHoursDraft);

  // Refs for unstable props — avoids cascading re-renders
  const onInstructorChangeRef = useRef(onInstructorChange);
  const instructorRef = useRef(instructor);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    onInstructorChangeRef.current = onInstructorChange;
  }, [onInstructorChange]);

  useEffect(() => {
    instructorRef.current = instructor;
  }, [instructor]);

  /**
   * Fetch leave count for THIS instructor from Journey data.
   * Only depends on stable values (batchNo, email).
   * Uses refs for callbacks to avoid triggering re-renders.
   */
  const fetchLeaveCount = useCallback(async () => {
    if (!batchNo || !instructor.email) return;
    try {
      setLoadingLeaveCount(true);
      const count = await getInstructorLeaveCount(batchNo, instructor.email);
      setJourneyLeaveCount(count);

      // Persist the leave count back to the batch document via parent state
      if (count !== instructorRef.current.trainerLeaves) {
        onInstructorChangeRef.current(index, "trainerLeaves", count, isEditBatch);
      }
    } catch (err) {
      console.error("Failed to fetch leave count", err);
    } finally {
      setLoadingLeaveCount(false);
    }
    // Only depends on stable values — NOT on onInstructorChange or instructor.trainerLeaves
  }, [batchNo, instructor.email, index, isEditBatch]);

  /**
   * Full refresh: recalculates leave counts for ALL instructors in the batch
   * and saves directly to the backend. Used by the refresh button.
   */
  const handleRefreshLeaves = useCallback(async () => {
    if (!batchNo) return;
    try {
      setLoadingLeaveCount(true);
      // Sync ALL instructor leave counts to batch DB in one call
      await syncBatchInstructorLeaves(batchNo);
      // Then refresh the local display
      const count = await getInstructorLeaveCount(batchNo, instructor.email || "");
      setJourneyLeaveCount(count);
      if (count !== instructorRef.current.trainerLeaves) {
        onInstructorChangeRef.current(index, "trainerLeaves", count, isEditBatch);
      }
    } catch (err) {
      console.error("Failed to refresh leave count", err);
    } finally {
      setLoadingLeaveCount(false);
    }
  }, [batchNo, instructor.email, index, isEditBatch]);

  /**
   * Manual correction of attendedHours — superAdmin only.
   * Calls the dedicated backend endpoint directly (separate from the
   * general batch save, which never persists attendedHours) so the audit
   * log captures who made the change.
   */
  const handleUpdateAttendedHours = async () => {
    if (!batchNo || !instructor.email) return;
    if (parsedDraftHours === null) {
      showNotification({
        title: "Invalid Attended Hours",
        message: 'Use a format like "16h 55m", "16:55" or "16.92"',
        color: "red",
      });
      return;
    }
    try {
      setSavingAttendedHours(true);
      const res = await ApiService.getInstance().put(
        "/batches/manual-update-attended-hours",
        {
          batch_no: batchNo,
          trainerEmail: instructor.email,
          attendedHours: parsedDraftHours,
        },
      );
      const updatedHours = String(res.data?.updatedHours ?? parsedDraftHours);
      onInstructorChangeRef.current(index, "attendedHours", updatedHours, isEditBatch);
      setAttendedHoursDraft(formatDecimalHoursToTime(updatedHours) || "");
      showNotification({
        title: "Attended Hours Updated",
        message: `Updated to ${formatDecimalHoursToTime(updatedHours) || "0h"} (${updatedHours} hrs)`,
        color: "green",
      });
    } catch (err: any) {
      showNotification({
        title: "Failed to Update Attended Hours",
        message: err?.response?.data?.error || "Something went wrong",
        color: "red",
      });
    } finally {
      setSavingAttendedHours(false);
    }
  };

  // Auto-fetch leave count ONCE on mount (per batchNo + email)
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [batchNo, instructor.email]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchLeaveCount();
  }, [fetchLeaveCount]);

  const courseBatch = courseName && batchNo ? `${courseName}-${batchNo}` : "";

  // Format student count with singular/plural
  const formatStudentCount = (count: number | string | undefined) => {
    const num = parseInt(String(count || 0));
    return num === 1
      ? `${num} registered Student`
      : `${num} registered Students`;
  };

  // Format amount
  const formatAmount = (amount: number | string | undefined) => {
    if (!amount) return "Not specified";
    return `INR ${amount}`;
  };

  // Format payment terms
  const getPaymentTermsDisplay = (paymentTermsText: string | undefined) => {
    if (!paymentTermsText) return "Not specified";

    const percentageMatch = paymentTermsText.match(/(\d+)%/);
    const percentage = percentageMatch ? percentageMatch[1] : "100";

    return `${percentage}% Completion`;
  };

  /**
   * Fetch MOU status and count from backend
   */
  const fetchMOUStatus = useCallback(async () => {
    if (!courseBatch || !instructor.email) return;

    try {
      const res = await ApiService.getInstance().get("/mou/status", {
        params: {
          courseBatch,
          trainerEmail: instructor.email,
        },
      });

      setMouStatus(res.data?.status ?? null);
      setMouCount(res.data?.mouCount ?? 0);
    } catch (err) {
      console.error("Failed to fetch MOU status");
    }
  }, [courseBatch, instructor.email]);

  /**
   * Fetch MOU history for popover
   */
  const fetchMOUHistory = async () => {
    if (!courseBatch || !instructor.email) return;

    setLoadingHistory(true);
    try {
      const res = await ApiService.getInstance().get("/mou/history", {
        params: {
          courseBatch,
          trainerEmail: instructor.email,
        },
      });

      setMouHistory(res.data?.mous ?? []);
    } catch (err) {
      console.error("Failed to fetch MOU history");
    } finally {
      setLoadingHistory(false);
    }
  };

  /**
   * Initial fetch + polling until Signed / Declined / Expired
   */
  useEffect(() => {
    fetchMOUStatus();

    if (mouStatus && !["Signed", "Declined", "Expired"].includes(mouStatus)) {
      const interval = setInterval(fetchMOUStatus, 15000); // 15 sec
      return () => clearInterval(interval);
    }
  }, [fetchMOUStatus, mouStatus]);

  const handleEmailSelection = (email: string | null) => {
    if (onInstructorEmailChange) {
      onInstructorEmailChange(index, email, isEditBatch);
    } else {
      onInstructorChange(index, "email", email || "", isEditBatch);
    }
  };

  const handleFieldChange = (field: keyof Instructor, value: any) => {
    onInstructorChange(index, field, value, isEditBatch);
  };
  // Helper function to handle numeric fields - removes leading zeros and handles empty values
  const handleNumericChange = (field: keyof Instructor, value: string) => {
    if (value === "" || value === "0") {
      handleFieldChange(
        field,
        field === "mouAmount"
          ? undefined
          : field === "trainerLeaves"
            ? undefined
            : "",
      );
    } else {
      const cleanValue = value.replace(/^0+/, "") || "0";
      if (field === "mouAmount") {
        handleFieldChange(field, parseFloat(cleanValue) || undefined);
      } else if (field === "trainerLeaves") {
        handleFieldChange(field, parseInt(cleanValue) || undefined);
      } else {
        handleFieldChange(field, cleanValue);
      }
    }
  };

  const getDisplayValue = (value: number | string | undefined): string => {
    if (!value || value === "0") return "";
    return String(value);
  };

  /**
   * Validate MOU data before showing preview
   */
  const validateMOUData = (): { valid: boolean; message?: string } => {
    if (!batchNo || !courseName) {
      return { valid: false, message: "Course or Batch missing" };
    }

    if (!instructor.name || !instructor.email) {
      return { valid: false, message: "Instructor name/email required" };
    }

    if (!instructor.mouAmount) {
      return { valid: false, message: "MOU amount required" };
    }

    if (!instructor.numOfStud || String(instructor.numOfStud) === "0") {
      return { valid: false, message: "Number of students is required" };
    }

    return { valid: true };
  };

  /**
   * Show Preview Modal
   */
  const handleShowPreview = () => {
    const validation = validateMOUData();

    if (!validation.valid) {
      showNotification({
        title: "Validation Error",
        message: validation.message,
        color: "red",
        icon: <IconX size={16} />,
      });
      return;
    }

    setPreviewOpened(true);
  };

  /**
   * SEND MOU - Called from Preview Modal
   */
  const handleSendMOU = async () => {
    try {
      setSendingMOU(true);

      const response = await ApiService.getInstance().post("/mou/send", {
        templateId: "42519000001370063",
        trainerName: instructor.name,
        trainerEmail: instructor.email,
        courseBatch,
        mouAmount: String(instructor.mouAmount),
        mouHours: instructor.mouHours || "",
        numOfStud: parseInt(String(instructor.numOfStud)) || 0,
        paymentTermsText: instructor.paymentTermsText,
        mouSignDate:
          instructor.dateOfAssign || new Date().toISOString().split("T")[0],
        trainerActionId: "42519000001370084",
      });

      showNotification({
        title: "Success",
        message: `MOU sent successfully (Version ${response.data.data.mouCount})`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      await fetchMOUStatus();
      setPreviewOpened(false);
    } catch (error) {
      showNotification({
        title: "Error",
        message: "Failed to send MOU",
        color: "red",
        icon: <IconX size={16} />,
      });
    } finally {
      setSendingMOU(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box
      mb="md"
      p="sm"
      style={{
        border: "1px solid #eee",
        borderRadius: 6,
        position: "relative",
      }}
    >
      <Group style={{ position: "absolute", top: 10, right: 10 }} gap="xs">
        {mouCount > 0 && (
          <Popover width={350} position="bottom" withArrow shadow="md">
            <Popover.Target>
              <Badge
                color="blue"
                size="sm"
                style={{ cursor: "pointer" }}
                onClick={fetchMOUHistory}
                leftSection={<IconHistory size={14} />}
              >
                MOU Count: {mouCount}
              </Badge>
            </Popover.Target>
            <Popover.Dropdown>
              <Text fw={600} size="sm" mb="xs">
                MOU History
              </Text>
              {loadingHistory ? (
                <Group justify="center" p="md">
                  <Loader size="sm" />
                </Group>
              ) : mouHistory.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No history available
                </Text>
              ) : (
                <Stack gap="xs">
                  {mouHistory.map((mou, idx) => (
                    <Box key={mou.requestId}>
                      <Group justify="space-between">
                        <Text size="xs" fw={500}>
                          Version {mouHistory.length - idx}
                        </Text>
                        <Badge
                          size="xs"
                          color={
                            mou.status === "Signed"
                              ? "green"
                              : mou.status === "Expired" ||
                                  mou.status === "Declined"
                                ? "red"
                                : "blue"
                          }
                        >
                          {mou.status}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {formatDate(mou.createdAt)}
                      </Text>
                      {mou.numOfStud && (
                        <Text size="xs" c="dimmed">
                          Students: {mou.numOfStud}
                        </Text>
                      )}
                      {idx < mouHistory.length - 1 && <Divider my="xs" />}
                    </Box>
                  ))}
                </Stack>
              )}
            </Popover.Dropdown>
          </Popover>
        )}

        {mouStatus && (
          <Badge
            color={
              mouStatus === "Signed"
                ? "green"
                : mouStatus === "Expired" || mouStatus === "Declined"
                  ? "red"
                  : "blue"
            }
            size="sm"
          >
            {mouStatus}
          </Badge>
        )}
      </Group>

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Course / Batch" value={courseBatch} readOnly />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Trainer Email ID"
            data={instructorsDropdown}
            value={instructor.email || ""}
            onChange={handleEmailSelection}
            searchable
            required
            placeholder="Select Email"
            nothingFoundMessage="Nothing found..."
            disabled={!editMode}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Trainer Name"
            value={instructor.name || ""}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            readOnly
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Date of Assign"
            type="date"
            value={instructor.dateOfAssign || ""}
            onChange={(e) => handleFieldChange("dateOfAssign", e.target.value)}
            readOnly={!editMode}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="MOU Hours"
            value={instructor.mouHours || ""}
            onChange={(e) => handleFieldChange("mouHours", e.target.value)}
            readOnly={!editMode}
            placeholder="Enter hours"
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="MOU Amount"
            value={getDisplayValue(instructor.mouAmount)}
            onChange={(e) => handleNumericChange("mouAmount", e.target.value)}
            readOnly={!editMode}
            placeholder="Enter amount"
            styles={{
              input: {
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                MozAppearance: "textfield",
              },
            }}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <Group gap={4} mb={4} align="center">
              <Text fw={600} size="sm" style={{ color: "#374151" }}>
                Leaves
              </Text>
              <Tooltip label="Refresh leave count from Journey data and save to batch">
                <Button
                  variant="subtle"
                  size="compact-xs"
                  color="blue"
                  loading={loadingLeaveCount}
                  onClick={handleRefreshLeaves}
                  style={{ padding: "2px 6px", minHeight: "unset", height: 22 }}
                >
                  {!loadingLeaveCount && <IconRefresh size={14} />}
                </Button>
              </Tooltip>
            </Group>
            <TextInput
              value={
                loadingLeaveCount
                  ? "Loading..."
                  : journeyLeaveCount !== null
                    ? String(journeyLeaveCount)
                    : getDisplayValue(instructor.trainerLeaves)
              }
              readOnly
              placeholder="0"
              styles={{
                input: {
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  fontWeight: 600,
                  color:
                    journeyLeaveCount && journeyLeaveCount > 0
                      ? "#ea580c"
                      : "#374151",
                  cursor: "default",
                },
              }}
            />
          </Box>
        </Grid.Col>

        {/* <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Attended Hours"
            value={instructor.attendedHours || ""}
            onChange={(e) => handleFieldChange("attendedHours", e.target.value)}
            readOnly={!editMode}
            placeholder="Enter attended hours"
          />
        </Grid.Col> */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Group gap={6} align="flex-end">
            <TextInput
              label="Attended Hours"
              value={attendedHoursDraft}
              onChange={(e) => setAttendedHoursDraft(e.target.value)}
              placeholder="e.g. 16h 55m"
              error={
                attendedHoursDraft.trim() && parsedDraftHours === null
                  ? 'Use "16h 55m", "16:55" or "16.92"'
                  : undefined
              }
              style={{ flex: 1 }}
            />
            <Tooltip label="Save attended hours">
              <Button
                variant="light"
                color="green"
                size="xs"
                loading={savingAttendedHours}
                disabled={
                  !batchNo ||
                  !instructor.email ||
                  parsedDraftHours === null ||
                  parsedDraftHours === (parseFloat(instructor.attendedHours || "") || 0)
                }
                onClick={handleUpdateAttendedHours}
              >
                Update
              </Button>
            </Tooltip>
          </Group>
          {parsedDraftHours !== null && (
            <Text size="xs" c="dimmed" mt={4}>
              Saved as {parsedDraftHours} hrs
            </Text>
          )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Payment Terms"
            data={[
              {
                value:
                  "25% payment will be processed upon the completion of 25% course",
                label: "25% Completion",
              },
              {
                value:
                  "50% payment will be processed upon the completion of 50% course",
                label: "50% Completion",
              },
              {
                value:
                  "75% payment will be processed upon the completion of 75% course",
                label: "75% Completion",
              },
              {
                value:
                  "100% payment will be processed upon the completion of 100% course",
                label: "100% Completion",
              },
            ]}
            value={instructor.paymentTermsText || ""}
            onChange={(v) => handleFieldChange("paymentTermsText", v)}
            placeholder="Select payment terms"
            // readOnly={!editMode}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Number of Students"
            value={instructor.numOfStud ? String(instructor.numOfStud) : ""}
            onChange={(e) => handleNumericChange("numOfStud", e.target.value)}
            placeholder="Enter number of students"
            // readOnly={!editMode}
          />
        </Grid.Col>
      </Grid>

      <Group justify="space-between" mt="md">
        {!editMode && instructor.email && (
          <Tooltip label="Preview MOU details before sending">
            <Button
              onClick={handleShowPreview}
              disabled={sendingMOU}
              leftSection={<IconEye size={16} />}
              variant="light"
            >
              {mouCount > 0
                ? `Preview & Resend (${mouCount + 1})`
                : "Preview & Send MOU"}
            </Button>
          </Tooltip>
        )}

        {editMode && (
          <Button
            variant="outline"
            color="red"
            size="xs"
            onClick={() => onRemove(index)}
          >
            Remove
          </Button>
        )}
      </Group>

      {/* MOU Preview Modal */}
      <Modal
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
        title={
          <Group gap="xs">
            <Text fw={600} size="lg">
              MOU Preview
            </Text>
            <Badge color="blue" variant="light">
              Review Before Sending
            </Badge>
          </Group>
        }
        size="xl"
        centered
      >
        <Stack gap="md">
          {/* Course and Batch Information */}
          <Paper p="md" withBorder>
            <Text fw={600} size="sm" mb="xs" c="dimmed">
              COURSE & BATCH DETAILS
            </Text>
            <Grid>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Course/Batch
                </Text>
                <Text fw={500}>{courseBatch || "Not specified"}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Number of Students
                </Text>
                <Text fw={500}>{formatStudentCount(instructor.numOfStud)}</Text>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Trainer Information */}
          <Paper p="md" withBorder>
            <Text fw={600} size="sm" mb="xs" c="dimmed">
              TRAINER DETAILS
            </Text>
            <Grid>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Trainer Name
                </Text>
                <Text fw={500}>{instructor.name || "Not specified"}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Trainer Email
                </Text>
                <Text fw={500}>{instructor.email || "Not specified"}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Date of Assignment
                </Text>
                <Text fw={500}>
                  {instructor.dateOfAssign
                    ? new Date(instructor.dateOfAssign).toLocaleDateString(
                        "en-IN",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                    : "Not specified"}
                </Text>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* MOU Terms */}
          <Paper p="md" withBorder>
            <Text fw={600} size="sm" mb="xs" c="dimmed">
              MOU TERMS
            </Text>
            <Grid>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  MOU Amount
                </Text>
                <Text fw={600} size="lg" c="blue">
                  {formatAmount(instructor.mouAmount)}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  MOU Hours
                </Text>
                <Text fw={500}>{instructor.mouHours || "Not specified"}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Payment Terms
                </Text>
                <Text fw={500}>
                  {getPaymentTermsDisplay(instructor.paymentTermsText)}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Trainer Leaves
                </Text>
                <Text fw={500}>{instructor.trainerLeaves || "0"}</Text>
              </Grid.Col>
              {instructor.attendedHours && (
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed">
                    Attended Hours
                  </Text>
                  <Text fw={500}>{instructor.attendedHours}</Text>
                </Grid.Col>
              )}
            </Grid>
          </Paper>

          {/* Payment Terms Details */}
          <Paper p="md" withBorder style={{ backgroundColor: "#f8f9fa" }}>
            <Text fw={600} size="sm" mb="xs">
              Payment Terms Details
            </Text>
            <Text size="sm">
              {instructor.paymentTermsText?.includes("100%")
                ? "The consultant's 100% payment will be processed upon the completion of 100% course duration, contingent upon meeting the minimum hours of required classes. For recordkeeping purposes, CONSULTANT must provide an invoice to Thinkcloudly in order to receive payment."
                : (() => {
                    const percentageMatch =
                      instructor.paymentTermsText?.match(/(\d+)%/);
                    const percentage = percentageMatch
                      ? percentageMatch[1]
                      : "100";
                    return `The consultant's ${percentage}% payment will be processed upon the completion of ${percentage}% course duration and the rest amount will be paid after completion of 100% of the course duration, contingent upon meeting the minimum hours of required classes. For recordkeeping purposes, CONSULTANT must provide an invoice to Thinkcloudly in order to receive payment.`;
                  })()}
            </Text>
          </Paper>

          <Divider />

          {/* Action Buttons */}
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setPreviewOpened(false)}
              leftSection={<IconX size={16} />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendMOU}
              loading={sendingMOU}
              leftSection={<IconSend size={16} />}
            >
              {sendingMOU ? "Sending..." : "Confirm & Send MOU"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};
